import React, { useMemo, useState } from "react";
import {
  XIcon,
  CalendarIcon,
  OfficeBuildingIcon,
  ChartBarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ViewGridIcon,
} from "@heroicons/react/outline";

const colorForValue = (val, max) => {
  if (max === 0) return { bg: "#f3f4f6", border: "#e5e7eb" };
  const ratio = val / Math.max(1, max);
  if (ratio >= 0.66) return { bg: "#10b981", border: "#059669" }; // green
  if (ratio >= 0.33) return { bg: "#f59e0b", border: "#d97706" }; // amber
  return { bg: "#ef4444", border: "#dc2626" }; // red
};

function RoomInventoryCalendar({ availabilityData }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);

  const {
    room_type_codes: roomTypeCodes = [],
    dates = [],
    calendar = [],
  } = availabilityData || {};

  const dateMap = useMemo(() => {
    const m = {};
    (calendar || []).forEach((d) => {
      m[d.date] = d;
    });
    return m;
  }, [calendar]);

  const maxPerRoom = useMemo(() => {
    const maxes = {};
    (roomTypeCodes || []).forEach((code) => (maxes[code] = 0));
    (calendar || []).forEach((d) => {
      const ra = d.room_availability || {};
      Object.keys(ra).forEach((code) => {
        maxes[code] = Math.max(maxes[code] || 0, ra[code] || 0);
      });
    });
    return maxes;
  }, [roomTypeCodes, calendar]);

  // Calculate total availability per date
  const totalPerDate = useMemo(() => {
    const totals = {};
    dates.forEach((d) => {
      const dateObj = dateMap[d];
      const ra = dateObj?.room_availability || {};
      totals[d] = Object.values(ra).reduce((sum, val) => sum + (val || 0), 0);
    });
    return totals;
  }, [dates, dateMap]);

  const handleScroll = (direction) => {
    const container = document.getElementById("calendar-scroll");
    if (container) {
      const scrollAmount = 300;
      const newPosition =
        direction === "left"
          ? scrollPosition - scrollAmount
          : scrollPosition + scrollAmount;
      container.scrollTo({ left: newPosition, behavior: "smooth" });
      setScrollPosition(newPosition);
    }
  };

  if (!availabilityData || !Array.isArray(dates) || dates.length === 0) {
    return (
      <div className="empty-state-container">
        <div className="empty-state-icon">
          <CalendarIcon className="w-16 h-16" />
        </div>
        <h3 className="empty-state-title">No Availability Data</h3>
        <p className="empty-state-text">
          Room availability information will appear here once data is loaded.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="calendar-wrapper">
        {/* Header */}
        <div className="calendar-header">
          <div className="header-content">
            <div className="header-icon">
              <ViewGridIcon className="w-6 h-6" />
            </div>
            <div>
              <h3 className="header-title">Room Availability Calendar</h3>
              <p className="header-subtitle">
                Click any cell to view detailed availability
              </p>
            </div>
          </div>

          {/* Stats Summary */}
          <div className="stats-summary">
            <div className="stat-item">
              <OfficeBuildingIcon className="stat-icon" />
              <div>
                <div className="stat-value">{roomTypeCodes.length}</div>
                <div className="stat-label">Room Types</div>
              </div>
            </div>
            <div className="stat-item">
              <CalendarIcon className="stat-icon" />
              <div>
                <div className="stat-value">{dates.length}</div>
                <div className="stat-label">Days</div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Arrows */}
        <div className="navigation-arrows">
          <button onClick={() => handleScroll("left")} className="nav-arrow">
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <button onClick={() => handleScroll("right")} className="nav-arrow">
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="calendar-container" id="calendar-scroll">
          <table className="calendar-table">
            <thead>
              <tr>
                <th className="room-type-header">
                  <div className="header-cell">
                    <OfficeBuildingIcon className="w-4 h-4" />
                    <span>Room Type</span>
                  </div>
                </th>
                {dates.map((d) => {
                  const dateObj = dateMap[d];
                  const label = dateObj?.date_string || d;
                  const total = totalPerDate[d] || 0;
                  return (
                    <th
                      key={d}
                      className="date-header"
                      onClick={() =>
                        setSelectedDate(
                          dateObj || {
                            date: d,
                            date_string: label,
                            room_availability: {},
                          }
                        )
                      }
                    >
                      <div className="date-header-content">
                        <div className="date-label">{label}</div>
                        <div className="date-sublabel">{d}</div>
                        <div className="date-total">
                          <span className="total-badge">{total} rooms</span>
                        </div>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {roomTypeCodes.map((code, idx) => (
                <tr key={code} className="room-row">
                  <td className="room-type-cell">
                    <div className="room-type-badge" style={{ 
                      background: `linear-gradient(135deg, ${
                        ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'][idx % 5]
                      }, ${
                        ['#7c3aed', '#2563eb', '#059669', '#d97706', '#dc2626'][idx % 5]
                      })`
                    }}>
                      {code.toUpperCase()}
                    </div>
                  </td>
                  {dates.map((d) => {
                    const dateObj = dateMap[d] || { room_availability: {} };
                    const val = dateObj.room_availability?.[code] ?? 0;
                    const max = maxPerRoom[code] || 1;
                    const colors = colorForValue(val, max);
                    return (
                      <td
                        key={`${code}-${d}`}
                        className="availability-cell"
                        onClick={() => setSelectedDate(dateObj)}
                      >
                        <div className="cell-content">
                          <div className="availability-value">{val}</div>
                          <div className="progress-bar-container">
                            <div
                              className="progress-bar"
                              style={{
                                width: `${Math.round(
                                  (val / Math.max(1, max)) * 100
                                )}%`,
                                background: colors.bg,
                              }}
                            />
                          </div>
                          <div className="availability-label">available</div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="legend">
          <span className="legend-title">Availability:</span>
          <div className="legend-items">
            <div className="legend-item">
              <div className="legend-color" style={{ background: '#10b981' }}></div>
              <span>High (66%+)</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ background: '#f59e0b' }}></div>
              <span>Medium (33-66%)</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ background: '#ef4444' }}></div>
              <span>Low (&lt;33%)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedDate && (
        <div className="modal-overlay">
          <div className="modal-backdrop" onClick={() => setSelectedDate(null)} />
          <div className="modal-container">
            {/* Modal Header */}
            <div className="modal-header">
              <div className="modal-header-content">
                <div className="modal-icon">
                  <CalendarIcon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="modal-title">
                    {selectedDate.date_string || selectedDate.date}
                  </h3>
                  <p className="modal-subtitle">Room availability breakdown</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedDate(null)}
                className="modal-close-btn"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="modal-body">
              {Object.entries(selectedDate.room_availability || {}).length > 0 ? (
                <div className="availability-grid">
                  {Object.entries(selectedDate.room_availability || {}).map(
                    ([code, val], idx) => {
                      const max = maxPerRoom[code] || 1;
                      const colors = colorForValue(val, max);
                      return (
                        <div key={code} className="availability-card">
                          <div className="card-header">
                            <div className="card-icon" style={{
                              background: `linear-gradient(135deg, ${
                                ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'][idx % 5]
                              }, ${
                                ['#7c3aed', '#2563eb', '#059669', '#d97706', '#dc2626'][idx % 5]
                              })`
                            }}>
                              <OfficeBuildingIcon className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="card-title">{code.toUpperCase()}</div>
                              <div className="card-subtitle">Room Type</div>
                            </div>
                          </div>
                          <div className="card-value">{val}</div>
                          <div className="card-label">Available Rooms</div>
                          <div className="card-progress">
                            <div
                              className="card-progress-bar"
                              style={{
                                width: `${Math.round((val / max) * 100)}%`,
                                background: colors.bg,
                              }}
                            />
                          </div>
                          <div className="card-percentage">
                            {Math.round((val / max) * 100)}% of capacity
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              ) : (
                <div className="modal-empty">
                  <ChartBarIcon className="w-12 h-12 text-gray-400" />
                  <p className="text-gray-600">No availability data for this date</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Styles */}
      <style jsx>{`
        .calendar-wrapper {
          background: white;
          border-radius: 1rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          overflow: hidden;
        }

        .calendar-header {
          padding: 1.5rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .header-icon {
          width: 3rem;
          height: 3rem;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .header-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: white;
          margin: 0;
        }

        .header-subtitle {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.9);
          margin: 0.25rem 0 0 0;
        }

        .stats-summary {
          display: flex;
          gap: 1.5rem;
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 0.75rem;
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .stat-icon {
          width: 2rem;
          height: 2rem;
          color: white;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: white;
        }

        .stat-label {
          font-size: 0.75rem;
          color: rgba(255, 255, 255, 0.9);
        }

        .navigation-arrows {
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          display: flex;
          justify-content: space-between;
          pointer-events: none;
          z-index: 10;
          padding: 0 1rem;
        }

        .nav-arrow {
          width: 2.5rem;
          height: 2.5rem;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          pointer-events: all;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          transition: all 0.2s ease;
        }

        .nav-arrow:hover {
          background: #f3f4f6;
          transform: scale(1.1);
        }

        .calendar-container {
          overflow-x: auto;
          position: relative;
        }

        .calendar-container::-webkit-scrollbar {
          height: 8px;
        }

        .calendar-container::-webkit-scrollbar-track {
          background: #f3f4f6;
        }

        .calendar-container::-webkit-scrollbar-thumb {
          background: #8b5cf6;
          border-radius: 4px;
        }

        .calendar-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }

        .room-type-header {
          position: sticky;
          left: 0;
          z-index: 20;
          background: #f9fafb;
          padding: 1rem;
          text-align: left;
          border-bottom: 2px solid #e5e7eb;
          min-width: 180px;
        }

        .header-cell {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .date-header {
          padding: 1rem;
          text-align: center;
          background: #f9fafb;
          border-bottom: 2px solid #e5e7eb;
          border-left: 1px solid #e5e7eb;
          min-width: 140px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .date-header:hover {
          background: #f3f4f6;
        }

        .date-header-content {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .date-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #1f2937;
        }

        .date-sublabel {
          font-size: 0.75rem;
          color: #9ca3af;
        }

        .date-total {
          margin-top: 0.5rem;
        }

        .total-badge {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          color: white;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .room-row {
          border-bottom: 1px solid #f3f4f6;
          transition: background 0.2s ease;
        }

        .room-row:hover {
          background: #fafafa;
        }

        .room-type-cell {
          position: sticky;
          left: 0;
          z-index: 10;
          background: white;
          padding: 1rem;
          border-right: 1px solid #e5e7eb;
        }

        .room-row:hover .room-type-cell {
          background: #fafafa;
        }

        .room-type-badge {
          display: inline-block;
          padding: 0.5rem 1rem;
          border-radius: 0.5rem;
          color: white;
          font-size: 0.875rem;
          font-weight: 700;
          letter-spacing: 0.05em;
        }

        .availability-cell {
          padding: 1rem;
          text-align: center;
          border-left: 1px solid #f3f4f6;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .availability-cell:hover {
          background: #f9fafb;
          transform: scale(1.05);
        }

        .cell-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
        }

        .availability-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
        }

        .progress-bar-container {
          width: 4rem;
          height: 0.5rem;
          background: #f3f4f6;
          border-radius: 9999px;
          overflow: hidden;
        }

        .progress-bar {
          height: 100%;
          border-radius: 9999px;
          transition: width 0.3s ease;
        }

        .availability-label {
          font-size: 0.75rem;
          color: #9ca3af;
        }

        .legend {
          padding: 1rem 1.5rem;
          background: #f9fafb;
          border-top: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          gap: 1.5rem;
          flex-wrap: wrap;
        }

        .legend-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
        }

        .legend-items {
          display: flex;
          gap: 1.5rem;
          flex-wrap: wrap;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: #6b7280;
        }

        .legend-color {
          width: 1rem;
          height: 1rem;
          border-radius: 0.25rem;
        }

        .empty-state-container {
          padding: 4rem 2rem;
          background: white;
          border-radius: 1rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          text-align: center;
        }

        .empty-state-icon {
          margin: 0 auto 1.5rem;
          color: #9ca3af;
        }

        .empty-state-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
          margin: 0 0 0.5rem 0;
        }

        .empty-state-text {
          font-size: 1rem;
          color: #6b7280;
          margin: 0;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          z-index: 50;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
        }

        .modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
        }

        .modal-container {
          position: relative;
          background: white;
          border-radius: 1rem;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          width: 100%;
          max-width: 900px;
          max-height: 80vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .modal-header {
          padding: 1.5rem;
          background: linear-gradient(135deg, #8b5cf6, #6366f1);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .modal-icon {
          width: 3rem;
          height: 3rem;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .modal-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: white;
          margin: 0;
        }

        .modal-subtitle {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.9);
          margin: 0.25rem 0 0 0;
        }

        .modal-close-btn {
          padding: 0.5rem;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          border-radius: 0.5rem;
          color: white;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .modal-close-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: scale(1.1);
        }

        .modal-body {
          padding: 2rem;
          overflow-y: auto;
        }

        .availability-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 1.5rem;
        }

        .availability-card {
          padding: 1.5rem;
          background: linear-gradient(135deg, #f9fafb, #ffffff);
          border: 1px solid #e5e7eb;
          border-radius: 1rem;
          transition: all 0.3s ease;
        }

        .availability-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        .card-icon {
          width: 2.5rem;
          height: 2.5rem;
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .card-title {
          font-size: 1rem;
          font-weight: 700;
          color: #1f2937;
        }

        .card-subtitle {
          font-size: 0.75rem;
          color: #9ca3af;
        }

        .card-value {
          font-size: 2.5rem;
          font-weight: 800;
          color: #1f2937;
          margin-bottom: 0.5rem;
        }

        .card-label {
          font-size: 0.875rem;
          color: #6b7280;
          margin-bottom: 1rem;
        }

        .card-progress {
          width: 100%;
          height: 0.5rem;
          background: #f3f4f6;
          border-radius: 9999px;
          overflow: hidden;
          margin-bottom: 0.5rem;
        }

        .card-progress-bar {
          height: 100%;
          border-radius: 9999px;
          transition: width 0.3s ease;
        }

        .card-percentage {
          font-size: 0.75rem;
          color: #9ca3af;
          text-align: right;
        }

        .modal-empty {
          padding: 4rem 2rem;
          text-align: center;
          color: #6b7280;
        }

        @media (max-width: 768px) {
          .calendar-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .stats-summary {
            width: 100%;
            justify-content: space-between;
          }

          .availability-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </>
  );
}

export default RoomInventoryCalendar;